import React, { useState, useEffect } from "react";
import axios from "axios";
import supabase from "../../../Components/Database/Supabase";
import { updateTaskStatusAPI, getTasksAPI } from "../../../Action/api";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  LinearProgress,
  Stack,
  Badge,
  Alert,
  CircularProgress,
} from "@mui/material";
import { 
  EmojiEvents, 
  AllInclusive, 
  Pending, 
  DirectionsRun, 
  RateReview, 
  CheckCircle, 
  ErrorOutline,
  PlaylistAdd,
  FactCheck,
  RateReviewOutlined,
  AssignmentTurnedIn,
  HourglassEmpty,
  Add
} from "@mui/icons-material";

// Empty state components for each tab
const EmptyStateComponent = ({ type }) => {
  const getEmptyStateContent = () => {
    switch (type) {
      case "To Do":
        return {
          icon: <PlaylistAdd style={{ fontSize: 60, color: "#757575" }} />,
          title: "No tasks in To Do yet",
          message: "Tasks that need to be started will appear here once assigned by your manager.",
          color: "#757575",
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          showManagerNote: true
        };
      case "Progress":
        return {
          icon: <DirectionsRun style={{ fontSize: 60, color: "#0288d1" }} />,
          title: "No tasks in Progress",
          message: "When you start working on assigned tasks, they will appear here. Move tasks from 'To Do' to track what you're currently working on.",
          color: "#0288d1",
          backgroundColor: "rgba(2, 136, 209, 0.04)",
          showManagerNote: true
        };
      case "Review":
        return {
          icon: <RateReviewOutlined style={{ fontSize: 60, color: "#ed6c02" }} />,
          title: "No tasks in Review",
          message: "Tasks waiting for review will appear here. Move tasks from 'Progress' when they're ready for feedback from your manager.",
          color: "#ed6c02",
          backgroundColor: "rgba(237, 108, 2, 0.04)",
          showManagerNote: true
        };
      case "Done":
        return {
          icon: <AssignmentTurnedIn style={{ fontSize: 60, color: "#2e7d32" }} />,
          title: "No tasks completed yet",
          message: "Your completed tasks will appear here. Keep up the good work and complete tasks assigned by your manager!",
          color: "#2e7d32",
          backgroundColor: "rgba(46, 125, 50, 0.04)",
          showManagerNote: true
        };
      case "Overdue":
        return {
          icon: <HourglassEmpty style={{ fontSize: 60, color: "#d32f2f" }} />,
          title: "No overdue tasks",
          message: "Great job! You don't have any tasks with missed deadlines. Tasks automatically appear here when their due dates have passed.",
          color: "#d32f2f",
          backgroundColor: "rgba(211, 47, 47, 0.04)",
          showManagerNote: false,
          showPenaltyNote: true
        };
      default:
        return {
          icon: <PlaylistAdd style={{ fontSize: 60, color: "#757575" }} />,
          title: "No tasks found",
          message: "Your workspace is currently empty. Tasks assigned by your manager will appear here when available.",
          color: "#757575",
          backgroundColor: "rgba(0, 0, 0, 0.04)",
          showManagerNote: true
        };
    }
  };

  const content = getEmptyStateContent();

  return (
    <Box 
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center",
        textAlign: "center", 
        padding: 4,
        minHeight: "300px",
        width: "100%",
        animation: "fadeIn 0.5s ease-in",
        "@keyframes fadeIn": {
          "0%": { opacity: 0, transform: "translateY(20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" }
        }
      }}
    >
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          width: 100, 
          height: 100, 
          borderRadius: "50%", 
          backgroundColor: content.backgroundColor,
          marginBottom: 3
        }}
      >
        {content.icon}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: "600", color: content.color, mb: 2 }}>
        {content.title}
      </Typography>
      <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: "500px", mb: 3 }}>
        {content.message}
      </Typography>
      {content.showManagerNote && (
        <Typography 
          variant="body2" 
          sx={{ 
            padding: "10px 16px", 
            backgroundColor: "rgba(25, 118, 210, 0.08)",
            color: "#1976d2",
            borderRadius: "20px",
            border: "1px dashed rgba(25, 118, 210, 0.5)",
            fontStyle: "italic"
          }}
        >
          Note: Tasks can only be created by your manager or supervisor
        </Typography>
      )}
      {content.showPenaltyNote && (
        <Typography 
          variant="body2" 
          sx={{ 
            padding: "10px 16px", 
            backgroundColor: "rgba(211, 47, 47, 0.08)",
            color: "#d32f2f",
            borderRadius: "20px",
            border: "1px dashed rgba(211, 47, 47, 0.5)",
            fontStyle: "italic"
          }}
        >
          Performance Note: Overdue tasks will result in -1 point each in your performance rating
        </Typography>
      )}
    </Box>
  );
};

const MyTask = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("To Do"); // Default to To Do instead of All
  const [selectedTask, setSelectedTask] = useState(null); // For detailed view modal
  // Add new state to track which tasks are updating
  const [updatingTasks, setUpdatingTasks] = useState({});
  // Add state for success feedback
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState({ show: false, taskId: null, status: null });

  // Fetch tasks from API
  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const staffId = (localStorage.getItem("userId") || "uid0031@auj.com").trim(); // Get staffId from localStorage or use default and trim it
        
        // Call the centralized API function instead of making the axios call directly
        const response = await getTasksAPI(staffId);
        
        if (response.data && response.data.status && response.data.tasks) {
          // Transform API data to match our component's expected format
          const transformedTasks = response.data.tasks.map(task => ({
            id: task.TaskID,
            sk: task.SK,
            name: task.Title,
            status: task.Status === "In Review" ? "Review" : task.Status === "In Progress" ? "Progress" : task.Status === "Completed" ? "Done" : task.Status,
            priority: task.Priority,
            dueDate: task.DueDate,
            updatedDate: task.UpdatedAt,
            day: getDayFromDate(task.DueDate), // Helper function to get day from date
            scope: task.Description,
            expectedOutput: task.ExpectedOutput,
            taskType: task.TaskType,
            reportedBy: task.ReportedBy
          }));
          
          setTasks(transformedTasks);
          setError(null);
        } else {
          setError("Invalid response format from API");
        }
      } catch (err) {
        console.error("Error fetching tasks:", err);
        setError("Failed to load tasks. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  // Format date to DD-MMM-YYYY format
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString || ""; // Return original string if there's an error
    }
  };

  // Helper function to get day of the week from date string
  const getDayFromDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } catch (error) {
      return "Unknown";
    }
  };

  // Calculate remaining days to due date
  const calculateRemainingDays = (dueDate, status) => {
    // Don't show remaining days for Done tasks
    if (status === "Done") return null;
    
    try {
      const today = new Date();
      const due = new Date(dueDate);
      
      // Reset time part for accurate day calculation
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      
      // Calculate the difference in days
      const diffTime = due.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error("Error calculating remaining days:", error);
      return null;
    }
  };

  // Format the remaining days for display
  const formatRemainingDays = (remainingDays) => {
    if (remainingDays === null) return "";
    
    if (remainingDays === 0) return "Due today";
    if (remainingDays === 1) return "1 day left";
    if (remainingDays > 1) return `${remainingDays} days left`;
    if (remainingDays === -1) return "1 day overdue";
    return `${Math.abs(remainingDays)} days overdue`;
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Helper function to update task counts in 003_People_Master table
  const updateTaskCountsInSupabase = async (oldStatus, newStatus, userId) => {
    try {
      console.log(`Updating 003_People_Master table for user ${userId}: ${oldStatus} -> ${newStatus}`);
      
      // First, fetch the current counts for this user
      const { data, error } = await supabase
        .from('003_People_Master')
        .select('task_to_do_count, task_in_progress_count, task_in_review_count, task_in_done_count')
        .eq('login_id', userId)
        .single();
      
      if (error) {
        console.error("Error fetching current task counts:", error);
        return false;
      }
      
      if (!data) {
        console.error("No user record found in 003_People_Master for:", userId);
        return false;
      }
      
      console.log("Current task counts:", data);
      
      // Create an object to hold the updates
      const updates = {};
      
      // Decrement count for old status
      if (oldStatus === "To Do") {
        updates.task_to_do_count = Math.max(0, (data.task_to_do_count || 0) - 1);
      } else if (oldStatus === "Progress") {
        updates.task_in_progress_count = Math.max(0, (data.task_in_progress_count || 0) - 1);
      } else if (oldStatus === "Review") {
        updates.task_in_review_count = Math.max(0, (data.task_in_review_count || 0) - 1);
      } else if (oldStatus === "Done") {
        updates.task_in_done_count = Math.max(0, (data.task_in_done_count || 0) - 1);
      }
      
      // Increment count for new status
      if (newStatus === "To Do") {
        updates.task_to_do_count = (data.task_to_do_count || 0) + 1;
      } else if (newStatus === "Progress") {
        updates.task_in_progress_count = (data.task_in_progress_count || 0) + 1;
      } else if (newStatus === "Review") {
        updates.task_in_review_count = (data.task_in_review_count || 0) + 1;
      } else if (newStatus === "Done") {
        updates.task_in_done_count = (data.task_in_done_count || 0) + 1;
      }
      
      console.log("Updating counts with:", updates);
      
      // Update the 003_People_Master table
      const { error: updateError } = await supabase
        .from('003_People_Master')
        .update(updates)
        .eq('login_id', userId);
      
      if (updateError) {
        console.error("Error updating task counts:", updateError);
        return false;
      }
      
      console.log(`Successfully updated task counts for user ${userId}`);
      return true;
    } catch (err) {
      console.error("Exception in updateTaskCountsInSupabase:", err);
      return false;
    }
  };

  // Handle status change and update the updatedDate
  const handleStatusChange = async (taskId, newStatus) => {
    try {
      // Set this task as updating
      setUpdatingTasks(prev => ({ ...prev, [taskId]: true }));
      
      const userId = localStorage.getItem("userId");
      
      // Get the current task to know its old status
      const currentTask = tasks.find(task => task.id === taskId);
      const oldStatus = currentTask ? currentTask.status : null;
      const SK = currentTask ? currentTask.sk : null; // Get SK from the current task
      
      if (oldStatus === newStatus) {
        // No change needed
        setUpdatingTasks(prev => {
          const newState = { ...prev };
          delete newState[taskId];
          return newState;
        });
        return;
      }

      // Calculate points and record completion date when task is marked as Done
      let pointsEarned = 0;
      let actualDoneDate = null;
      
      if (newStatus === "Done") {
        actualDoneDate = new Date().toISOString().split('T')[0]; // Current date in YYYY-MM-DD format
        
        // Calculate points based on due date comparison
        const dueDate = new Date(currentTask.dueDate);
        const today = new Date();
        
        // Reset time part for accurate day calculation
        dueDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        
        if (today <= dueDate) {
          // Task completed on time or early - award +1 point
          pointsEarned = 1;
        } else {
          // Task completed late - penalty equals days overdue
          const diffTime = today.getTime() - dueDate.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          pointsEarned = -diffDays;
        }
        
        console.log(`Task ${taskId} completed with ${pointsEarned} points. Actual completion date: ${actualDoneDate}`);
      }
      
      // Prepare additional data to send with the status update
      const additionalData = newStatus === "Done" 
        ? { 
          TaskPoint: pointsEarned,
          ActualDoneDate: actualDoneDate
        } 
        : {}; 
      
      // Call the API to update the task status with SK as a separate parameter
      const response = await updateTaskStatusAPI(taskId, newStatus, SK, userId, additionalData);
      
      if (response.data && response.data.status) {
        // API call successful, update the frontend state
        const updatedTask = response.data.updatedTask;
        
        // Update task counts in Supabase (run in parallel with the UI updates)
        updateTaskCountsInSupabase(oldStatus, newStatus, userId).then(success => {
          if (success) {
            console.log("Successfully updated task counts in 003_People_Master table");
          } else {
            console.warn("Failed to update task counts in 003_People_Master table");
          }
        });
        
        // Show success notification
        setStatusUpdateSuccess({
          show: true,
          taskId: taskId,
          status: newStatus
        });
        
        // Clear success notification after 3 seconds
        setTimeout(() => {
          setStatusUpdateSuccess({ show: false, taskId: null, status: null });
        }, 3000);
        
        // Animate task movement by keeping it in the old status for a moment
        setTimeout(() => {
          setTasks((prevTasks) =>
            prevTasks.map((task) =>
              task.id === taskId
                ? { 
                    ...task, 
                    status: newStatus, 
                    updatedDate: updatedTask.UpdatedAt,
                    priority: updatedTask.Priority,
                    dueDate: updatedTask.DueDate,
                    scope: updatedTask.Description,
                    expectedOutput: updatedTask.ExpectedOutput
                  }
                : task
            )
          );
          
          // Also update the selected task if it's the one being modified
          if (selectedTask && selectedTask.id === taskId) {
            setSelectedTask((prevTask) => ({
              ...prevTask,
              status: newStatus,
              updatedDate: updatedTask.UpdatedAt,
              priority: updatedTask.Priority,
              dueDate: updatedTask.DueDate,
              scope: updatedTask.Description,
              expectedOutput: updatedTask.ExpectedOutput
            }));
          }
        }, 300);
        
      } else {
        console.error("Failed to update task status:", response.data.message || "Unknown error");
        // Optionally implement error handling UI feedback here
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      // Optionally implement error handling UI feedback here
    } finally {
      // Remove updating state after a slight delay to allow animation to complete
      setTimeout(() => {
        setUpdatingTasks(prev => {
          const newState = { ...prev };
          delete newState[taskId];
          return newState;
        });
      }, 500);
    }
  };

  // Get available next statuses based on current status
  const getAvailableStatuses = (currentStatus) => {
    switch (currentStatus) {
      case "To Do":
        return ["To Do", "Progress"];
      case "Progress":
        return ["Progress", "To Do", "Review"];
      case "Review":
        return ["Review", "Progress", "Done"];
      case "Done":
        return ["Done", "Review"];
      case "Overdue":
        return ["Overdue", "Done"]; // Overdue tasks can only move to Done
      default:
        return ["To Do", "Progress", "Review", "Done", "Overdue"];
    }
  };

  // Check if a task is overdue
  const isTaskOverdue = (task) => {
    // Don't consider Done tasks as overdue
    if (task.status === "Done") return false;
    
    try {
      const today = new Date();
      const dueDate = new Date(task.dueDate);
      
      // Reset time part for accurate day calculation
      today.setHours(0, 0, 0, 0);
      dueDate.setHours(0, 0, 0, 0);
      
      // Check if due date has passed
      return dueDate < today;
    } catch (error) {
      console.error("Error checking overdue status:", error);
      return false;
    }
  };

  // Calculate how many days a task is overdue
  const getDaysOverdue = (task) => {
    if (task.status === "Done" || !isTaskOverdue(task)) return 0;
    
    try {
      const today = new Date();
      const dueDate = new Date(task.dueDate);
      
      // Reset time part for accurate day calculation
      today.setHours(0, 0, 0, 0);
      dueDate.setHours(0, 0, 0, 0);
      
      // Calculate the difference in days
      const diffTime = today.getTime() - dueDate.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    } catch (error) {
      console.error("Error calculating days overdue:", error);
      return 0;
    }
  };
  
  // Calculate total penalty points for overdue tasks
  const calculatePenaltyPoints = (tasks) => {
    return tasks.reduce((total, task) => {
      const daysOverdue = getDaysOverdue(task);
      return total + daysOverdue;
    }, 0);
  };

  // Filter tasks based on the selected tab
  const filteredTasks = tasks.filter((task) => {
    if (selectedTab === "All") return true;
    
    // Special case for Overdue tab - show all tasks with past due dates
    if (selectedTab === "Overdue") {
      return isTaskOverdue(task);
    }
    
    // For other tabs, show tasks with matching status
    return task.status === selectedTab;
  });

  // Sort tasks in the "All" tab by updatedDate (most recent first)
  const sortedTasks =
    selectedTab === "All"
      ? [...filteredTasks].sort(
          (a, b) => new Date(b.updatedDate) - new Date(a.updatedDate)
        )
      : filteredTasks;

  // Get color for status chip
  const getStatusColor = (status) => {
    switch (status) {
      case "Done":
        return "success";
      case "Progress":
        return "info";
      case "To Do":
        return "default";
      case "Review":
        return "warning";
      case "Overdue":
        return "error";
      default:
        return "default";
    }
  };

  // Truncate long text for card view
  const truncateText = (text, maxLength) => {
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text || "";
  };

  // Calculate task metrics
  const calculateMetrics = () => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(task => task.status === "Done").length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const todoTasks = tasks.filter(task => task.status === "To Do").length;
    const inProgressTasks = tasks.filter(task => task.status === "Progress").length;
    
    return {
      totalTasks,
      completedTasks,
      completionRate,
      todoTasks,
      inProgressTasks
    };
  };

  // Array of motivational quotes
  const motivationalQuotes = [
    "The only way to do great work is to love what you do. – Steve Jobs",
    "Success is not final, failure is not fatal: It is the courage to continue that counts. – Winston Churchill",
    "Your time is limited, don't waste it living someone else's life. – Steve Jobs",
    "Quality is not an act, it is a habit. – Aristotle",
    "The future depends on what you do today. – Mahatma Gandhi",
    "Small progress is still progress. Keep going!"
  ];

  // Get a random quote
  const getRandomQuote = () => {
    const randomIndex = Math.floor(Math.random() * motivationalQuotes.length);
    return motivationalQuotes[randomIndex];
  };

  const metrics = calculateMetrics();
  const quote = getRandomQuote();

  return (
    <Box sx={{ padding: 4, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      
      {/* Success Notification */}
      {statusUpdateSuccess.show && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            top: 20, 
            right: 20, 
            zIndex: 2000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2,
            animation: 'fadeInOut 0.5s ease',
            '@keyframes fadeInOut': {
              '0%': { opacity: 0, transform: 'translateY(-20px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            }
          }}
          onClose={() => setStatusUpdateSuccess({ show: false, taskId: null, status: null })}
        >
          Task <strong>{statusUpdateSuccess.taskId}</strong> moved to <strong>{statusUpdateSuccess.status}</strong> successfully!
        </Alert>
      )}
  
      {/* Motivational Banner */}
      <Paper 
        elevation={4}
        sx={{
          padding: 3,
          marginBottom: 4,
          borderRadius: 3,
          backgroundImage: 'linear-gradient(to right, #8e2de2, #4a00e0)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Circle Elements */}
        <Box sx={{ 
          position: 'absolute', 
          width: '150px', 
          height: '150px', 
          borderRadius: '50%', 
          backgroundColor: 'rgba(255,255,255,0.1)', 
          top: '-50px', 
          right: '-20px' 
        }} />
        <Box sx={{ 
          position: 'absolute', 
          width: '100px', 
          height: '100px', 
          borderRadius: '50%', 
          backgroundColor: 'rgba(255,255,255,0.1)', 
          bottom: '-30px', 
          left: '10%' 
        }} />
        
        <Grid container spacing={4} alignItems="center">
          {/* Motivational Quote Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Today's Inspiration
            </Typography>
            <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
              "{quote}"
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmojiEvents sx={{ color: '#FFD700' }} />
              <Typography variant="body2">
                You've completed {metrics.completedTasks} tasks. Keep the momentum going!
              </Typography>
            </Stack>
          </Grid>

          {/* Metrics Section */}
          <Grid item xs={12} md={6}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1">Overall Progress</Typography>
                <Typography variant="body1" fontWeight="bold">{metrics.completionRate}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={metrics.completionRate} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4caf50'
                  }
                }} 
              />
              
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.2)', textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="h5" fontWeight="bold">{metrics.todoTasks}</Typography>
                    <Typography variant="body2">To Do</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.2)', textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="h5" fontWeight="bold">{metrics.inProgressTasks}</Typography>
                    <Typography variant="body2">Progress</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.2)', textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="h5" fontWeight="bold">{metrics.completedTasks}</Typography>
                    <Typography variant="body2">Done</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for Task Flow */}
      <Paper 
        elevation={3} 
        sx={{ 
          marginBottom: 4, 
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Static Background Element (removed animation) */}
        <Box 
          sx={{ 
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
            backgroundSize: '100% 100%',
            zIndex: 0,
          }} 
        />

        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          centered
          sx={{
            position: 'relative',
            zIndex: 1,
            padding: '6px',
            "& .MuiTab-root": {
              fontWeight: "bold",
              borderRadius: '12px',
              margin: '0 4px',
              padding: '10px 8px',
              transition: 'all 0.3s ease',
              minHeight: '48px',
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                color: '#1976d2',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-2px)',
              },
              '&:hover:not(.Mui-selected)': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                transform: 'translateY(-1px)',
              },
              '& .MuiBadge-badge': {
                fontSize: '0.7rem',
                height: '16px',
                minWidth: '16px',
                padding: '0 4px',
                boxShadow: '0 2px 5px rgba(25, 118, 210, 0.2)',
              },
              '& .MuiSvgIcon-root': {
                marginRight: { xs: '2px', sm: '4px', md: '8px' },
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                transition: 'transform 0.2s ease',
              },
              '&.Mui-selected .MuiSvgIcon-root': {
                transform: 'scale(1.2)',
              }
            },
            '& .MuiTabs-indicator': {
              height: '3px',
              borderRadius: '1.5px',
              boxShadow: '0 1px 3px rgba(25, 118, 210, 0.5)',
            },
          }}
        >
          <Tab 
            label={
              <Badge badgeContent={tasks.filter(task => task.status === "To Do").length} color="default" sx={{ '& .MuiBadge-badge': { fontWeight: 'bold' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Pending sx={{ color: selectedTab === "To Do" ? '#757575' : '#757575' }} />
                  <Box component="span" sx={{ ml: { xs: '2px', sm: '4px' } }}>To Do</Box>
                </Box>
              </Badge>
            } 
            value="To Do" 
          />
          <Tab 
            label={
              <Badge badgeContent={tasks.filter(task => task.status === "Progress").length} color="info" sx={{ '& .MuiBadge-badge': { fontWeight: 'bold' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DirectionsRun sx={{ color: selectedTab === "Progress" ? '#0288d1' : '#757575' }} />
                  <Box component="span" sx={{ ml: { xs: '2px', sm: '4px' } }}>Progress</Box>
                </Box>
              </Badge>
            } 
            value="Progress" 
          />
          <Tab 
            label={
              <Badge badgeContent={tasks.filter(task => task.status === "Review").length} color="warning" sx={{ '& .MuiBadge-badge': { fontWeight: 'bold' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RateReview sx={{ color: selectedTab === "Review" ? '#ed6c02' : '#757575' }} />
                  <Box component="span" sx={{ ml: { xs: '2px', sm: '4px' } }}>Review</Box>
                </Box>
              </Badge>
            } 
            value="Review" 
          />
          <Tab 
            label={
              <Badge badgeContent={tasks.filter(task => task.status === "Done").length} color="success" sx={{ '& .MuiBadge-badge': { fontWeight: 'bold' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ color: selectedTab === "Done" ? '#2e7d32' : '#757575' }} />
                  <Box component="span" sx={{ ml: { xs: '2px', sm: '4px' } }}>Done</Box>
                </Box>
              </Badge>
            } 
            value="Done" 
          />
          <Tab 
            label={
              <Badge badgeContent={tasks.filter(task => isTaskOverdue(task)).length} color="error" sx={{ '& .MuiBadge-badge': { fontWeight: 'bold' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ErrorOutline sx={{ color: selectedTab === "Overdue" ? '#d32f2f' : '#757575' }} />
                  <Box component="span" sx={{ ml: { xs: '2px', sm: '4px' } }}>Overdue</Box>
                </Box>
              </Badge>
            } 
            value="Overdue" 
          />
        </Tabs>
      </Paper>

      {/* Task Cards */}
      <Grid container spacing={3}>
        {/* Penalty Banner for Overdue Tab */}
        {selectedTab === "Overdue" && sortedTasks.length > 0 && (
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                padding: 2,
                mb: 2,
                backgroundColor: "rgba(211, 47, 47, 0.05)",
                border: "1px solid rgba(211, 47, 47, 0.2)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ErrorOutline color="error" sx={{ mr: 1.5 }} />
                <Typography sx={{ mr: 2 }}>
                  You have <strong>{sortedTasks.length}</strong> overdue task{sortedTasks.length !== 1 ? 's' : ''} with a total of <strong>{calculatePenaltyPoints(sortedTasks)}</strong> days overdue
                </Typography>
              </Box>
              <Chip
                label={`-${calculatePenaltyPoints(sortedTasks)} point${calculatePenaltyPoints(sortedTasks) !== 1 ? 's' : ''}`}
                color="error"
                variant="outlined"
                sx={{ 
                  fontWeight: "bold", 
                  fontSize: "0.9rem",
                  px: 1,
                  border: "2px solid"
                }}
              />
            </Paper>
            
            {/* Warning Levels Information */}
            <Paper
              elevation={2}
              sx={{
                padding: 2,
                mb: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: "#333" }}>
                Overdue Task Warning Levels
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 1, 
                    bgcolor: "rgba(255, 152, 0, 0.1)", 
                    border: "1px solid rgba(255, 152, 0, 0.3)",
                    textAlign: "center"
                  }}>
                    <Typography variant="body2" color="#ff9800" fontWeight="bold">-10 Points</Typography>
                    <Typography variant="caption" color="text.secondary">Informal Warning</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 1, 
                    bgcolor: "rgba(229, 57, 53, 0.1)", 
                    border: "1px solid rgba(229, 57, 53, 0.3)",
                    textAlign: "center"
                  }}>
                    <Typography variant="body2" color="#e53935" fontWeight="bold">-15 Points</Typography>
                    <Typography variant="caption" color="text.secondary">Formal Warning</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 1, 
                    bgcolor: "rgba(211, 47, 47, 0.1)", 
                    border: "1px solid rgba(211, 47, 47, 0.3)",
                    textAlign: "center"
                  }}>
                    <Typography variant="body2" color="#d32f2f" fontWeight="bold">-20 Points</Typography>
                    <Typography variant="caption" color="text.secondary">Final Warning Letter</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 1, 
                    bgcolor: "rgba(123, 31, 162, 0.1)", 
                    border: "1px solid rgba(123, 31, 162, 0.3)",
                    textAlign: "center"
                  }}>
                    <Typography variant="body2" color="#7b1fa2" fontWeight="bold">-25 Points</Typography>
                    <Typography variant="caption" color="text.secondary">Termination Consideration</Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Early completion bonus info */}
              <Box sx={{ 
                mt: 2, 
                p: 1.5, 
                borderRadius: 1, 
                bgcolor: "rgba(76, 175, 80, 0.1)", 
                border: "1px solid rgba(76, 175, 80, 0.3)"
              }}>
                <Typography variant="subtitle2" color="#2e7d32" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ fontSize: 20, mr: 1 }} /> Earn Points & Reduce Penalties
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Complete tasks 1 day before the due date to earn +1 point per task. These additional points will help offset any penalties from overdue tasks.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
        
        {sortedTasks.length === 0 ? (
          <EmptyStateComponent type={selectedTab} />
        ) : (
          sortedTasks.map((task) => (
            <Grid item xs={12} sm={6} md={4} key={task.id}>
              <Card
                elevation={3}
                onClick={() => setSelectedTask(task)}
                sx={{
                  borderRadius: 4, // More rounded corners
                  boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.1)", // Subtle shadow
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  transition: "transform 0.2s ease-in-out",
                  height: 320, // Fixed height for all cards
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  "&:hover": {
                    transform: "scale(1.03)", // Slightly larger on hover
                    boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                {/* Header Section */}
                <Box
                  sx={{
                    backgroundColor: getStatusColor(task.status) === "success" ? "#e8f5e9" : "#e3f2fd",
                    padding: 2,
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: getStatusColor(task.status) === "success" ? "#2e7d32" : "#1565c0",
                    }}
                  >
                    {task.name}
                  </Typography>
                </Box>

                {/* Content Section */}
                <CardContent
                  sx={{
                    padding: 2,
                    flexGrow: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Chip
                    label={task.status}
                    color={getStatusColor(task.status)}
                    sx={{ marginBottom: 1 }}
                  />
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    <strong>Task Number:</strong> {task.id}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Priority: <strong>{task.priority}</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Due Date: <strong>{formatDate(task.dueDate)}</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 3, // Limit to 3 lines
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    <strong>Scope:</strong> {task.scope}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 3, // Limit to 3 lines
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    <strong>Expected Output:</strong> {task.expectedOutput}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {formatRemainingDays(calculateRemainingDays(task.dueDate, task.status))}
                  </Typography>
                </CardContent>

                {/* Footer Section */}
                <Box
                  sx={{
                    padding: 2,
                    borderTop: "1px solid #e0e0e0",
                    textAlign: "center",
                    position: "relative"
                  }}
                >
                  {/* Loading overlay when status is changing */}
                  {updatingTasks[task.id] && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        zIndex: 2,
                        borderBottomLeftRadius: 4,
                        borderBottomRightRadius: 4,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setSelectedTask(task)}
                    sx={{ marginRight: 1 }}
                  >
                    Read More
                  </Button>
                  <Select
                    value={task.status}
                    onChange={(e) => handleStatusChange(task.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{ minWidth: 120 }}
                    disabled={updatingTasks[task.id]}
                  >
                    {getAvailableStatuses(task.status).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Detailed View Modal */}
      {selectedTask && (
        <Dialog open={Boolean(selectedTask)} onClose={() => setSelectedTask(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: "bold", color: "#333" }}>Task Details</DialogTitle>
          <DialogContent>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Task Number</TableCell>
                    <TableCell>{selectedTask.id}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Day</TableCell>
                    <TableCell>{selectedTask.day}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Task</TableCell>
                    <TableCell>{selectedTask.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Priority</TableCell>
                    <TableCell>{selectedTask.priority}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Scope</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-line' }}>{selectedTask.scope}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Expected Output</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-line' }}>{selectedTask.expectedOutput}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Due Date</TableCell>
                    <TableCell>{formatDate(selectedTask.dueDate)}</TableCell>
                  </TableRow>
                  {calculateRemainingDays(selectedTask.dueDate, selectedTask.status) !== null && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Remaining Days</TableCell>
                      <TableCell sx={{ 
                        color: calculateRemainingDays(selectedTask.dueDate, selectedTask.status) < 0 ? '#d32f2f' : 
                               calculateRemainingDays(selectedTask.dueDate, selectedTask.status) === 0 ? '#ed6c02' : '#2e7d32'
                      }}>
                        {formatRemainingDays(calculateRemainingDays(selectedTask.dueDate, selectedTask.status))}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Change Status</TableCell>
                    <TableCell>
                      {updatingTasks[selectedTask.id] ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">Updating status...</Typography>
                        </Box>
                      ) : (
                        <Select
                          value={selectedTask.status}
                          onChange={(e) => handleStatusChange(selectedTask.id, e.target.value)}
                          fullWidth
                          size="small"
                          disabled={updatingTasks[selectedTask.id]}
                        >
                          {getAvailableStatuses(selectedTask.status).map((status) => (
                            <MenuItem key={status} value={status}>
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default MyTask;